<template>
	<wrapper-features-templates-operations
	:scrollValue="scrollValue"
	:showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState && !showLoader"
	:errorStateMessage="t('cards.transfers.flex.errorStateMessage')"
	:showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && !!getCardsList.length"
	:activeStep="activeStep"
	:stepperParams="stepperParams"
	:showFooter="activeStep < 3 && !showLoader && !applyErrorState && !!getCardsList.length"
	:footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
	:feedbackOperationData="feedbackOperationData"
	@clearScrollToValue="scrollValue = null"
	@backOrCloseClicked="emit('backOrCloseClicked')">
		<!-- *********************************************
		*                 Reuse Transfer                 *
		********************************************** -->
		<button-tertiary
		v-if="activeStep === 1 && !applyErrorState && !showLoader && !!getCardsList.length"
		icon="cycle"
		buttonSize="small"
		style="align-self: center;"
		:label="t('cards.transfers.flex.btn.reuseTransfer')"
		@clicked="showReuseTransferModal = true"/>
		<!-- Reuse Transfer Modal -->
		<reuse-transfer-modal
		v-if="showReuseTransferModal"
		:getLastTransfers="getLastTransfersList"
		:getCardsList="getCardsList"
		@reuseTransferRowClicked="setDataFromReuseModal"
		@hideMe="showReuseTransferModal = false"/>

		<!-- *********************************************
		*                      Content                   *
		********************************************** -->
		<component
		v-if ="!applyErrorState && !showLoader && !!getCardsList.length"
		:is="getActiveStepComponent"
		:isMobile="getMobileAppState"
		:formData="formData"
		:getCardsList="getCardsList"
		:getFilteredCardsList="filteredCardsList"
		:clearFormErrors="clearFormErrors"
		:mandatoryList="mandatoryList"
		:showMandatoryErrors="showMandatoryErrors"
		:responseData="responseData"
		@formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
		@scrollTo="scrollValue = $event"
		@updateData="updateDataHandler"
		@newTransfer="newTransferHandler"/>
		<!-- Empty-state -->
		<template #empty-state-slot>
			<transition name="common-enter-animation" mode="out-in" appear>
				<empty-state-generic
				v-if="!applyErrorState && !showLoader && !getCardsList.length"
				illustration="emptyState/cards-flex"
				:title="t('cards.transfers.flex.emptyState.title')"
				:description="t('cards.transfers.flex.emptyState.description')"/>
			</transition>
		</template>
	</wrapper-features-templates-operations>
	<!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'
import ReuseTransferModal from '@/components/features/cards/transfers/flex/ReuseTransferModal'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, watch, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
	requestCardsList,
	requestLastTransfersList,
	creditTransfer,
	clearFeatureStates
} = mapActions(moduleMapper.CARDS_TRANSFER)

const { getCardsList, getLastTransfersList } = mapGetters(moduleMapper.CARDS_TRANSFER)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showReuseTransferModal = ref(false)

/* Data */
const filteredCardsList = ref('')
const applyErrorState = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const props = defineProps({
	selectedCardId: [String, Number]
})

/*************************************************
*                    Steps Data                  *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
	{ description: t('cards.transfers.flex.stepper.data') },
	{ description: t('cards.transfers.flex.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
	const stepNumber = activeStep.value
	return defineAsyncComponent(() => import('@/components/features/cards/transfers/flex/Step' + stepNumber))
})
/*************************************************
*                Form Logic Data                 *
*************************************************/
const formData = ref({
	originCard: '',
	destinationCard: '',
	amount: ''
})

const mandatoryList = ref([
	'originCard',
	'destinationCard',
	'amount'
])

const newTransferHandler = () => {
	showOverlayLoader.value = true
	goToStep(1)
	clearFormData()
	initialRequests()
}

const updateDataHandler = ({ fieldName, inputValue }) => {
	if (fieldName === 'amount') {
		formData.value[fieldName] = inputValue
		return
	}

	formData.value[fieldName] = inputValue
	filterDestinationCards()
}

const filterDestinationCards = () => {
	if (formData.value.originCard) {
		filteredCardsList.value = getCardsList.value.filter(card => card.id !== formData.value.originCard)

		/* changes destination card if it's the same as the origin only */
		if (!filteredCardsList.value.find(card => card.id !== formData.value.destinationCard) ||
			formData.value.originCard === formData.value.destinationCard) {
			formData.value.destinationCard = filteredCardsList.value[0].id
		}
	}
}
/*************************************************
*           Reuse Transfer Modal Logic           *
*************************************************/
const setDataFromReuseModal = (event) => {
	if (getCardsList.value.find(card => card.id === event.originCard)) {
		formData.value.originCard = event.originCard
	} else formData.value.originCard = null

	if (getCardsList.value.find(card => card.id === event.destinationCard)) {
		formData.value.destinationCard = event.destinationCard
	} else formData.value.destinationCard = null

	formData.value.amount = event.amount
	clearFormErrors.value = true

	/* If theres no origin card, makes all destination cards available */
	if (!formData.value.originCard) { filteredCardsList.value = getCardsList.value }
}
/*************************************************
*             Submit Transfer Logic              *
*************************************************/
const submitTransfer = async () => {
	showOverlayLoader.value = true

	try {
		const transferResponse = await creditTransfer({
			destinationCardId: formData.value.destinationCard,
			originCardId: formData.value.originCard,
            amount: formData.value.amount
		})

		successHandler(transferResponse.data)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, submitTransfer)
	}
}
/*************************************************
*             Success/Error Handlers             *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const successHandler = async data => {
	responseData.value = data

	feedbackOperationData.value = {
		status: data.errorLevel,
		title: data.message,
		buttonsData: !data.data.hasReceipt ? null : {
			emailAction: 'sendOperationEmailNotification',
			smsAction: 'sendOperationSMSNotification',
			sendPayload: { operationId: data.data.operationId },
			downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
			shareLink: data.data.operationId,
			operationId: data.data.operationId
		}
	}

	showOverlayLoader.value = false
	goToStep(3)
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(2) })

	showLoader.value = false
	showOverlayLoader.value = false
	showFeedbackToast.value = true
}
/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

const clearFormData = () => {
	clearFormErrors.value = true
	clearObjectStrings(formData.value)
	filteredCardsList.value = null

	/* Reset form */
	formData.value.originCard = getCardsList.value[0].id
	filterDestinationCards()
	formData.value.destinationCard = filteredCardsList.value[0].id
	formData.value.currency = getCardsList.value[0].cardCurrency

	feedbackOperationData.value = null
	responseData.value = null
	showOverlayLoader.value = false
}

const validateMandatoryFormValues = () => {
	if (formData.value.amount <= 0 || atLeastOneIsEmpty(formData.value, mandatoryList.value) || invalidFields()) {
		showMandatoryErrors.value = true
	} else goToStep(2)
}

/* After validating mandatory fields, check for valid card inputs */
const invalidFields = () => {
	return !(!!formData.value.originCard || !!formData.value.destinationCard)
}

/* Footer Buttons Data */
const footerButtonsParams = ref([
	{
		label: t('cards.transfers.flex.btn.clear'),
		buttonPosition: 'left',
		callbackFunction: clearFormData,
		step: 1
	}, {
		label: t('cards.transfers.flex.btn.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		step: 1,
		callbackFunction: validateMandatoryFormValues
	}, {
		label: t('cards.transfers.flex.btn.edit'),
		icon: 'arrow-left-full',
		step: 2,
		callbackFunction: () => { activeStep.value-- }
	}, {
		label: t('cards.transfers.flex.btn.validate'),
		icon: 'check-line',
		buttonType: 'primary',
		iconPosition: 'right',
		step: 2,
		callbackFunction: () => { submitTransfer() }
	}
])
/*************************************************
*                Initial Requets                 *
*************************************************/
const initialRequests = async () => {
	applyErrorState.value = false
	showLoader.value = true

	/* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
	Promise.all([
	requestCardsList(),
	requestLastTransfersList({ pageSize: 10 })
	]).then(() => {
		if (getCardsList.value.length > 0) {
			/* Setting initial info for selected cards */
			/* Saves selectedCard prop (id only) if exists */
			formData.value.originCard = props.selectedCardId ? props.selectedCardId : getCardsList.value[0].id
			formData.value.destinationCard = getCardsList.value.filter(card => card.id !== formData.value.originCard)[0].id
			/* Filter destination cards */
			filteredCardsList.value = getCardsList.value.filter(card => card.id !== formData.value.originCard)
			/* Get currency */
			formData.value.currency = getCardsList.value[0].cardCurrency
		}

		showLoader.value = false
		goToStep(1)
	}).catch(error => {
		applyErrorState.value = true
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, initialRequests)
	})
}
watch(() => activeStep.value, (newVal, oldVal) => {
	scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.button-primary {
	min-width: 200px;

	&.mobile {
		width: 100%;
	}
}
</style>
